import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ShoppingCart } from 'lucide-react'
import { Button } from '@/components/ui'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { toggleCart, selectCartItems, selectCartTotals } from '@/store/cartSlice'
import { formatPrice } from '@/utils/formatting'

export function FloatingCartButton() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const items = useAppSelector(selectCartItems)
  const totals = useAppSelector(selectCartTotals)
  const [footerOverlap, setFooterOverlap] = useState(0)

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)

  // Push FAB above the footer when it enters the viewport
  useEffect(() => {
    const footer = document.querySelector('footer')
    if (!footer) return

    let rafId: number

    const update = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const rect = footer.getBoundingClientRect()
        const viewportH = window.innerHeight
        if (rect.top < viewportH) {
          setFooterOverlap(viewportH - rect.top)
        } else {
          setFooterOverlap(0)
        }
      })
    }

    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update, { passive: true })
    update()

    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      cancelAnimationFrame(rafId)
    }
  }, [])

  if (items.length === 0) return null

  const bottom = Math.max(16, footerOverlap + 16)

  return (
    <Button
      onClick={() => dispatch(toggleCart())}
      style={{ bottom }}
      className="fixed left-1/2 -translate-x-1/2 sm:left-auto sm:right-6 sm:translate-x-0 z-[60] shadow-xl hover:shadow-2xl h-14 px-6 text-base gap-2.5 animate-in slide-in-from-bottom duration-300 bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 transition-all rounded-full"
    >
      <ShoppingCart className="h-5 w-5" />
      <span className="font-semibold">
        {t('common:yourCart')} ({totalItems})
      </span>
      <span className="font-semibold">
        &middot; {formatPrice(totals.total, items[0]?.menuItem?.currency)}
      </span>
    </Button>
  )
}
